import { useEffect, useState } from "react";

/*
 * Report text fitting.
 *
 * The weekly status reports render user text in fixed-width cells using the proportional Figtree font, so the
 * number of characters that fit on a line depends on which characters are typed. Instead of capping the
 * character count, the text is measured on a canvas with the same font and size as the report and compared
 * with the width of the report cell.
 *
 * Every layout below mirrors one report cell. To retune a cell after checking the rendered report, only
 * `cellWidthPx` (and, for multi-line boxes, `maxLines`) has to change.
 */

const REPORT_FONT_FAMILY = "Figtree";
const REPORT_FONT_WEIGHT = 400;
const PT_TO_PX = 96 / 72;

// Space left unused on each line to absorb rounding and kerning differences between the canvas and the
// renderer that produces the report.
export const SAFETY_MARGIN_PX = 4;

// Left + right padding (2px each) and the collapsed table borders of a report cell.
const CELL_PADDING_PX = 4;
const CELL_BORDER_PX = 2;

// The report prefixes each KPI status text with a yyMMdd date. Digits are measured as zeros.
const DATE_PREFIX = "000000: ";

const buildLayout = ({ cellWidthPx, fontPt, maxLines, prefix = "" }) => ({
    font: `${REPORT_FONT_WEIGHT} ${(fontPt * PT_TO_PX).toFixed(3)}px ${REPORT_FONT_FAMILY}`,
    widthPx: cellWidthPx - CELL_PADDING_PX - CELL_BORDER_PX - SAFETY_MARGIN_PX,
    maxLines,
    prefix,
});

export const REPORT_LAYOUTS = {
    // Project WSR, `.current-period-table td`: Accomplishments, Planned, Next Period Planned, Communication.
    currentPeriodLine: buildLayout({ cellWidthPx: 450, fontPt: 7 }),
    // Project WSR, `.project-health-table .status`: text next to the KPIs, one line per period.
    kpiStatusLine: buildLayout({ cellWidthPx: 770, fontPt: 7, prefix: DATE_PREFIX }),
    // Project WSR, `.major-risks-table td`: Major Risks and Mitigation.
    majorRisksBox: buildLayout({ cellWidthPx: 460, fontPt: 7, maxLines: 4 }),
    // Project WSR, `.major-risks-table td`: Major Dependencies.
    majorDependenciesBox: buildLayout({ cellWidthPx: 460, fontPt: 7, maxLines: 4 }),
    // Project WSR, `.pm-special-concerns-table td`: Special Concerns from PM.
    specialConcernsBox: buildLayout({ cellWidthPx: 900, fontPt: 7, maxLines: 2 }),
    // Program WSR, `.focus-box-item`: a third of the 1586px page.
    programFocusLine: buildLayout({ cellWidthPx: 1586 / 3, fontPt: 7.5 }),
};

let context = null;

const getContext = () => {
    if (!context) {
        context = document.createElement("canvas").getContext("2d");
    }
    return context;
};

const measure = (text, font) => {
    const ctx = getContext();
    ctx.font = font;
    return ctx.measureText(text).width;
};

// Loading is idempotent; the returned promise resolves once the report font is available for measuring.
export const loadReportFonts = () => {
    if (!document.fonts) {
        return Promise.resolve();
    }
    const fonts = Object.values(REPORT_LAYOUTS).map(({ font }) => document.fonts.load(font));
    return Promise.all(fonts).catch(() => { });
};

// Re-renders the calling component once the report font is loaded, since widths measured with a fallback font
// are wrong.
export const useReportFonts = () => {
    const [, setLoaded] = useState(false);
    useEffect(() => {
        let active = true;
        loadReportFonts().then(() => active && setLoaded(true));
        return () => { active = false; };
    }, []);
};

// Greedy word wrap matching `white-space: normal`: lines break at spaces, explicit newlines start a new line
// (an empty line still occupies a row), and a word wider than the cell is broken by character.
// Returns the number of rows and the width used by the last row.
const measureWrapped = (text, { font, widthPx }) => {
    let lines = 0;
    let lastLineWidth = 0;

    text.split("\n").forEach((paragraph) => {
        let line = "";
        lines++;

        paragraph.split(" ").forEach((word) => {
            const candidate = line ? `${line} ${word}` : word;
            if (measure(candidate, font) <= widthPx) {
                line = candidate;
                return;
            }
            if (measure(word, font) <= widthPx) {
                lines++;
                line = word;
                return;
            }
            // Word wider than a whole row: fill the current row, then continue on new rows.
            let chunk = line ? `${line} ` : "";
            for (const char of word) {
                if (measure(chunk + char, font) > widthPx && chunk) {
                    lines++;
                    chunk = "";
                }
                chunk += char;
            }
            line = chunk;
        });

        lastLineWidth = measure(line, font);
    });

    return { lines, lastLineWidth };
};

const isMultiLine = (layout) => Boolean(layout.maxLines);

export const fitsLayout = (text, layout) => {
    if (isMultiLine(layout)) {
        return measureWrapped(text, layout).lines <= layout.maxLines;
    }
    return measure(layout.prefix + text, layout.font) <= layout.widthPx;
};

// 0 for empty text, 1 when the text uses the whole cell (one line) or every available row (multi-line).
export const getFillRatio = (text, layout) => {
    if (!text) {
        return 0;
    }
    if (isMultiLine(layout)) {
        const { lines, lastLineWidth } = measureWrapped(text, layout);
        const usedPx = (lines - 1) * layout.widthPx + lastLineWidth;
        return Math.min(1, usedPx / (layout.maxLines * layout.widthPx));
    }
    return Math.min(1, measure(layout.prefix + text, layout.font) / layout.widthPx);
};

// Longest prefix of `text` that fits. Fitting is monotonic in the prefix length, so a binary search is enough.
export const truncateToFit = (text, layout) => {
    if (fitsLayout(text, layout)) {
        return text;
    }
    let low = 0;
    let high = text.length;
    while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        if (fitsLayout(text.slice(0, mid), layout)) {
            low = mid;
        } else {
            high = mid - 1;
        }
    }
    return text.slice(0, low);
};

// Decides the value an input keeps after an edit:
// - text that fits is accepted;
// - text that got shorter is accepted, so content saved before the limit existed can still be trimmed;
// - a single extra character that does not fit is rejected, like a maxLength;
// - a larger insertion (paste) is cut to the longest prefix that fits.
export const constrainInput = (next, previous, layout) => {
    if (fitsLayout(next, layout) || next.length < previous.length) {
        return next;
    }
    if (next.length - previous.length <= 1) {
        return previous;
    }
    return truncateToFit(next, layout);
};
